import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getData, getSelected, getLoading, getAssetsComposition } from '../selectors';
import '../styles.scss';
import * as finderActions from '../actions';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Empty } from '@myua/kit';
import { Total } from './Total';
import { Loading } from '../model';
import { AssetList } from './AssetList';
import { Sorting } from '../../sorting/components/Sorting';
import { AssetGridSkeleton } from '../../skeleton/components/AssetGridSkeleton';
import { ProgressBar } from '../../skeleton/components/Skeleton';

// `emptyContent` replaces the default "No files found" (the catalog offers an upload there).
export const Assets: React.FC<{ header?: React.ReactNode; emptyContent?: React.ReactNode }> = ({
  header,
  emptyContent,
}) => {
  const dispatch = useDispatch();
  const selected = useSelector(getSelected);
  const { assets, total } = useSelector(getData);
  const empty = total.read.count === 0;
  const loading = useSelector(getLoading);
  const { gapHorizontal } = useSelector(getAssetsComposition);

  // Первая загрузка: показывать нечего, вместо спиннера рисуем сетку-скелетон в той же
  // геометрии, что и будущие карточки. Подгрузка следующей страницы и тихий фоновый
  // рефетч сюда не попадают — там на экране уже есть контент.
  const showSkeleton = loading === Loading.refresh && assets.length === 0;

  const style = useMemo(
    () => ({
      marginLeft: -gapHorizontal / 2,
      marginRight: -gapHorizontal / 2,
    }),
    [gapHorizontal],
  );

  // Список уже на экране, а данные перезапрашиваются (сменили фильтр, сортировку):
  // тонкая полоса вместо перекрывающего всё спиннера. Живёт над самим списком, а не
  // над всем блоком: сверху строка со счётчиком и сортировкой, и полоса наезжала на
  // выпадашку «New first».
  const showProgress = loading === Loading.refresh && !showSkeleton;

  return (
    <div className="al-finder-assets">
      <div className="al-finder-assets__top">
        <Total loading={showSkeleton} />
        {header}
        <div className="al-finder-assets__top-space" />
        <Sorting />
      </div>
      <div
        className="al-finder-assets__assets"
        // Скелетоны и полоса прогресса для скринридера скрыты (aria-hidden), поэтому о
        // загрузке сообщает сама область — раньше это неявно делал <Spin>.
        aria-busy={loading !== Loading.none}
        style={style}
        onClick={
          selected.ids.length > 0
            ? () =>
                dispatch(
                  finderActions.assetSelected({ ids: [], firstId: undefined, lastId: undefined }),
                )
            : undefined
        }
      >
        {showProgress && <ProgressBar />}
        <AutoSizer style={{ width: '100%' }}>
          {({ width, height }) => {
            if (showSkeleton) {
              return <AssetGridSkeleton width={width} height={height} />;
            }

            if (empty && loading === Loading.none) {
              return (
                <div style={{ width, height }} className="al-finder-assets__empty">
                  {emptyContent ?? <Empty description="No files found" />}
                </div>
              );
            }

            return <AssetList width={width} height={height} />;
          }}
        </AutoSizer>
      </div>
    </div>
  );
};
